import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useQuery } from '@hooks';
import { utils, hotkeys } from '@ohif/core';
import {
  OneTimeAccessModal,
  SwitchButton,
  useSessionConfiguration,
  useViewportGrid,
} from '@ohif/ui';

import {
  Icon,
  StudyListExpandedRow,
  Button,
  EmptyStudies,
  StudyListTable,
  StudyListPagination,
  StudyListFilter,
  TooltipClipboard,
  Header,
  useModal,
  AboutModal,
  UserPreferences,
  LoadingIndicatorProgress,
  ExportDiagnosisModal,
} from '@ohif/ui';

import i18n from '@ohif/i18n';

const { sortBySeriesDate } = utils;

const { availableLanguages, defaultLanguage, currentLanguage } = i18n;

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  servicesManager,
}) {
  const {
    CarianApiService,
    UserAuthenticationService,
    UIModalService,
  } = servicesManager.services;

  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const query = useQuery();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 101;
  const queryFilterValues = _getQueryFilterValues(query);
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...queryFilterValues,
  });
  const [{ layout: checked }, api] = useSessionConfiguration();

  function handleToggle() {
    api.setLayoutToggle(!checked);
  }
  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;
  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */
  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort
      ? { sortBy: 'studyDate', sortDirection: 'descending' }
      : {};
  const sortedStudies = studies;
  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  const [diagnosisData, setDiagnosisData] = useState({});
  const [count, setCount] = useState();
  const numOfStudies = studiesTotal?.count;

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    setExpandedRows([]);
  };

  const nextPage = newPageNumber => {
    _setFilterValues({
      ...filterValues,
      pageNumber: newPageNumber,
    });
  };

  const onPageNumberChange = newPageNumber => {
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(
      studiesTotal / filterValues.resultsPerPage
    );
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage =
      Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  // filterValues

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString = {};
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];
      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (
          currValue.startDate &&
          defaultValue.startDate !== currValue.startDate
        ) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (key === 'status' && currValue.length) {
        queryString.status = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({
      pathname: '/studies',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, series);
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].id;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }
      fetchSeries(studyInstanceUid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const tableDataSource = sortedStudies.map((study, key) => {
    const rowKey = key + 1;

    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      id,
      dicomId: studyInstanceUid,
      id: accession,
      modalities,
      instances,
      description,
      date,
      time,
      status,
    } = study;
    const patientName = `${study?.patientFirstName ??
      ''} ${study?.patientLastName ?? ''}`;
    const mrn = study?.patientDocumentNumber ?? '';
    const patient = study?.patientDocumentNumber ?? '';

    const statusColors = {
      New: 'blue',
      Open: 'yellow',
      Diagnosed: 'green',
    };
    const statusColor = statusColors[status] ? statusColors[status] : 'red';
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format('MMM-DD-YYYY');
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format('hh:mm A');
    return {
      row: [
        {
          key: 'patientName',
          content: patientName ? (
            <TooltipClipboard>{patientName}</TooltipClipboard>
          ) : (
            <span className="text-gray-700">(Empty)</span>
          ),
          gridCol: 4,
        },
        {
          key: 'mrn',
          content: <TooltipClipboard>{mrn}</TooltipClipboard>,
          gridCol: 3,
        },
        {
          key: 'studyDate',
          content: <>{moment(date).format('MMM-DD-YYYY hh:mm A')}</>,
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 5,
        },
        {
          key: 'description',
          content: <TooltipClipboard>{description}</TooltipClipboard>,
          gridCol: 4,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 3,
        },
        {
          key: 'instances',
          content: (
            <>
              <Icon
                name="group-layers"
                className={classnames('inline-flex mr-2 w-4', {
                  'text-primary-active': isExpanded,
                  'text-secondary-light': !isExpanded,
                })}
              />
              {instances}
            </>
          ),
          title: (instances || 0).toString(),
          gridCol: 4,
        },
        {
          key: 'status',
          content: (
            <>
              <abbr title={status}>
                <div
                  style={{
                    height: '10px',
                    width: '10px',
                    backgroundColor: statusColor,
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}
                ></div>
              </abbr>
            </>
          ),
          title: 'Status',
          gridCol: 1,
        },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      expandedContent: (
        <StudyListExpandedRow
          seriesTableColumns={{
            description: 'Description',
            seriesNumber: 'Series',
            modality: 'Modality',
            instances: 'Instances',
          }}
          seriesTableDataSource={
            seriesInStudiesMap.has(id)
              ? seriesInStudiesMap.get(id).map(s => {
                  return {
                    description: s.description || '(empty)',
                    seriesNumber: s.number ?? '',
                    modality: s.modality || '',
                    instances: s.instances || '',
                  };
                })
              : []
          }
        >
          {appConfig.modes.map((mode, i) => {
            const isFirst = i === 0;
            const isValidMode = modalities
              ? mode.isValidMode({ modalities })
              : false;
            // TODO: Modes need a default/target route? We mostly support a single one for now.
            // We should also be using the route path, but currently are not
            // mode.routeName
            // mode.routes[x].path
            // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
            // How do we know which params to pass? Today, it's just StudyInstanceUIDs
            const createCode = async () => {
              const oneTimeData = await CarianApiService.oneTimeAccess.createCode(
                id
              );
              UIModalService.show({
                layout: checked,
                content: OneTimeAccessModal,
                customClassName: `border-2 border-white bg-primary-dark rounded-2xl w-[450px] h-[320px] m-auto`,
                contentProps: {
                  servicesManager,
                  oneTimeData,
                  onClose: () => {
                    UIModalService.hide();
                  },
                },
              });
            };

            const findLastDiagnosis = async () => {
              const getDiagnosisData = await CarianApiService.diagnosis.latest(
                id
              );
              const diagnosisData = await {
                createdAt: getDiagnosisData?.createdAt ?? '',
                studyId: getDiagnosisData?.study?.dicomId ?? '',
                madeBy:
                  getDiagnosisData?.physician?.user?.lastName ??
                  '' + ' ' + getDiagnosisData?.physician?.user?.firstName ??
                  '',
                patientName:
                  getDiagnosisData?.study?.patient?.user?.lastName ??
                  '' +
                    ' ' +
                    getDiagnosisData?.study?.patient?.user?.firstName ??
                  '',
                patientId:
                  getDiagnosisData?.study?.patient?.documentNumber ?? '',
                patientBirthDate:
                  getDiagnosisData?.study?.patient?.birthDate ?? '',
                studyDescription: getDiagnosisData?.study?.description ?? '',
                studyDate: getDiagnosisData?.study?.date ?? '',
                // exportsDiagnosis={true}
                institutionName:
                  getDiagnosisData?.study?.physician?.clinicName ?? '',
                description: getDiagnosisData?.text ?? '',
                contact: getDiagnosisData?.study?.clinic?.phoneNumber ?? '',
                referringPhysician:
                  getDiagnosisData?.study?.physician?.user?.lastName ??
                  '' +
                    ' ' +
                    getDiagnosisData?.study?.physician?.user?.firstName ??
                  '',
                IDNo:
                  getDiagnosisData?.study?.clinic?.medicalEstablishment ?? '',
                labName: getDiagnosisData?.study?.clinic.name ?? '',
              };

              // const diagnosisData = await mapDiagnosisData?.[0];

              UIModalService.show({
                layout: checked,
                content: ExportDiagnosisModal,
                customClassName: `border-2 border-white bg-primary-dark rounded-2xl w-[450px] h-[320px] m-auto`,
                contentProps: {
                  servicesManager,
                  diagnosisData,
                  onClose: () => {
                    UIModalService.hide();
                  },
                },
              });
            };

            return (
              <>
                <div className="flex">
                  <Link
                    key={i}
                    to={`${dataPath ? '../../' : ''}${
                      mode.routeName
                    }${dataPath ||
                      ''}?StudyInstanceUIDs=${studyInstanceUid}&StudyInternalUIDs=${id}`}
                    // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
                  >
                    <Button
                      rounded="full"
                      variant={isValidMode ? 'contained' : 'disabled'}
                      disabled={!isValidMode}
                      endIcon={<Icon name="launch-arrow" />} // launch-arrow | launch-info
                      className={classnames('font-medium	', {
                        'ml-2': !isFirst,
                      })}
                      onClick={() => {}}
                    >
                      {t(`Modes:${mode.displayName}`)}
                    </Button>
                  </Link>
                  {status === 'Diagnosed' ? (
                    <>
                      <button
                        onClick={() => {
                          createCode();
                        }}
                        className="font-medium ml-2 px-2 rounded-full h-8 text-black items-center	flex content-center text-base w-15 bg-primary-light  p-4 self-center"
                      >
                        Generate One-Time Access
                      </button>
                      <button
                        onClick={() => {
                          findLastDiagnosis();
                        }}
                        className="font-medium ml-2 px-2 rounded-full h-8 text-black items-center	flex content-center text-base w-15 bg-primary-light  p-4 self-center"
                      >
                        Export Diagnosis
                      </button>
                    </>
                  ) : null}
                </div>
              </>
            );
          })}
        </StudyListExpandedRow>
      ),
      onClickRow: () =>
        setExpandedRows(s =>
          isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey]
        ),
      isExpanded,
    };
  });

  const hasStudies = numOfStudies > 0;
  const versionNumber = process.env.VERSION_NUMBER;
  const buildNumber = process.env.BUILD_NUM;

  const menuOptions = [
    {
      title: t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: 'Carian Viewer',
          contentProps: { versionNumber, buildNumber },
        }),
    },
    {
      title: t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          title: t('UserPreferencesModal:User Preferences'),
          content: UserPreferences,
          contentProps: {
            hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(
              hotkeyDefaults
            ),
            hotkeyDefinitions,
            onCancel: hide,
            currentLanguage: currentLanguage(),
            availableLanguages,
            defaultLanguage,
            onSubmit: state => {
              i18n.changeLanguage(state.language.value);
              hotkeysManager.setHotkeys(state.hotkeyDefinitions);
              hide();
            },
            onReset: () => hotkeysManager.restoreDefaultBindings(),
            hotkeysModule: hotkeys,
          },
        }),
    },
    {
      title: t('Header:Terms & Conditions'),
      onClick: () => {
        navigate(`/termsAndConditions`);
      },
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(
          `/logout?redirect_uri=${encodeURIComponent(
            window.location.protocol + window.location.host
          )}`
        );
      },
    });
  }

  return (
    <div
      className={classnames('bg-black h-full', {
        'h-screen': !hasStudies,
      })}
    >
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={true}
        onClickReturnButton={() => navigate('/')}
        WhiteLabeling={appConfig.whiteLabeling}
        swichButtonOn={
          <SwitchButton checked={checked} onClick={handleToggle} />
        }
      />
      <div className={`${checked ? 'w-[1200px]' : ''}`}>
        <StudyListFilter
          title={'Study list'}
          numOfStudies={numOfStudies}
          filtersMeta={filtersMeta}
          filterValues={{ ...filterValues, ...defaultSortValues }}
          onChange={setFilterValues}
          clearFilters={() => setFilterValues(defaultFilterValues)}
          isFiltering={isFiltering(filterValues, defaultFilterValues)}
        />
        {hasStudies ? (
          <>
            <StudyListTable
              tableDataSource={tableDataSource}
              numOfStudies={numOfStudies}
              filtersMeta={filtersMeta}
            />
            <StudyListPagination
              studiesLength={studies.length}
              onChangePage={onPageNumberChange}
              nextPage={nextPage}
              onChangePerPage={onResultsPerPageChange}
              currentPage={pageNumber}
              perPage={resultsPerPage}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center pt-48">
            {appConfig.showLoadingIndicator && isLoadingData ? (
              <LoadingIndicatorProgress className={'w-full h-full bg-black'} />
            ) : (
              <EmptyStudies />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  patientId: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: '',
  pageNumber: 1,
  resultsPerPage: 25,
  datasourcename: '',
  status: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str !== null) {
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseInt(str);
      }
    }
  }
  return retValue;
}

function _getQueryFilterValues(query) {
  const queryFilterValues = {
    patientName: query.get('patientName'),
    patientId: query.get('patientId'),
    studyDate: {
      startDate: query.get('startDate'),
      endDate: query.get('endDate'),
    },
    description: query.get('description'),
    modalities: query.get('modalities')
      ? query.get('modalities').split(',')
      : [],
    accession: query.get('accession'),
    sortBy: query.get('sortBy'),
    sortDirection: query.get('sortDirection'),
    pageNumber: _tryParseInt(query.get('pageNumber'), 1),
    resultsPerPage: _tryParseInt(query.get('resultsPerPage'), 25),
    datasourcename: query.get('datasourcename'),
    status: query.get('status'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (
      (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier
    );
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
